/**
 * @author Zdenka Valkučáková <zdenka.valkucakova@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-env browser */

import { defineComponent } from 'vue';

// @ts-ignore Není to ESM.
import template from './status-info-dialog-root.html';

import { $root } from '../../player/player';
import CommunicatorReport from '../communicator/report/communicator-report';
import CountdownRoot from '../countdown/countdown-root';
import focus from '../focus/focus-directive';
import StatusInfoDialogAdvanced from './status-info-dialog-advanced';
import StatusInfoDialogBasic from './status-info-dialog-basic';

/**
 * @import { PropType } from 'vue'
 * @import { StatusInfoPage } from '.'
 * @import { ContentInfo } from '../content/info/content-info'
 * @import { HeartbeatFetched } from '../heartbeat'
 * @import { ReportInfo } from '../report'
 * @import { ServerInfo } from '../server'
 */

/**
 * Stránky dialogu.
 * @enum {StatusInfoPage}
 */
const PageEnum = /** @type {const} */ ({
  Basic: 'basic',
  Advanced: 'advanced',
  Loader: 'loader'
});

// TODO: Bug v kombinácií so setup, kde pri pokuse o stlačení "X" sa
// focus berie aj z tlačítok v SetupQR. Asi im je nutné odobrať focus,
// alebo zrefaktorovať FocusNavigator, aby pri hľadaní aktívneho
// elemenetu bral do úvahy viditeľnosť elementu.
export default defineComponent({
  name: 'StatusInfoDialogRoot',
  template,
  components: {
    StatusInfoDialogBasic,
    StatusInfoDialogAdvanced,
    CountdownRoot
  },
  directives: { focus },
  data() {
    return {
      /** @type {number | null} */
      currentTimeMillis: null,
      /** @type {number} */
      inactivityThreshold: new Date().getTime(),
      /** @type {PageEnum} */
      page: PageEnum.Loader,
      /** @type {ReportInfo | undefined} */
      reportInfo: undefined,
      /** @type {ServerInfo} */
      serverInfo: $root(this).serverConnectionMonitor.getServerInfo(),
      /** @type {boolean} */
      show: false
    };
  },
  created() {
    this.onOpen = this.onOpen.bind(this);
    this.onClose = this.onClose.bind(this);
    this.onHeartbeatFetched = this.onHeartbeatFetched.bind(this);
    this.onActivity = this.onActivity.bind(this);
  },
  mounted() {
    const { communicator, statusInfoDialogController } = $root(this);
    communicator.on('heartbeatFetched', this.onHeartbeatFetched);
    statusInfoDialogController.on('activity', this.onActivity);
    statusInfoDialogController.on('close', this.onClose);
    statusInfoDialogController.on('open', this.onOpen);
  },
  destroyed() {
    const { communicator, statusInfoDialogController } = $root(this);
    communicator.off('heartbeatFetched', this.onHeartbeatFetched);
    statusInfoDialogController.off('activity', this.onActivity);
    statusInfoDialogController.off('close', this.onClose);
    statusInfoDialogController.off('open', this.onOpen);
  },
  methods: {
    addActiveClass(e) {
      addClassIfHTMLElement(this.$el, 'active');
    },
    removeActiveClass(e) {
      removeClassIfHTMLElement(this.$el, 'active');
    },
    /**
     * @param {Event} e
     */
    close(e) {
      $root(this).statusInfoDialogController.close();
      this.show = false;
    },
    async fetchReport() {
      const { reportManager } = $root(this);
      // contentInfo nie je povinné, preto vue pripúšta undefined,
      // ale reportManager.getInfo() očakáva ContentInfo alebo null.
      this.reportInfo = await reportManager.getInfo(this.contentInfo || null);
    },
    /**
     * @param {number} inactivityTime Doba "neaktivity" po které se dialog zavře. [ms]
     */
    onActivity(inactivityTime) {
      this.setInactivityThreshold(inactivityTime);
    },
    onClose() {
      $root(this).serverConnectionMonitor.off(
        'connectionChanged',
        this.onServerConnectionChanged
      );
      this.show = false;
    },
    /**
     * @param {HeartbeatFetched} heartbeatFetched
     */
    async onHeartbeatFetched(heartbeatFetched) {
      this.currentTimeMillis = heartbeatFetched.heartbeatInfo.currentTimeMillis;
      // Načítanie reportu.
      await this.fetchReport();
      this.setPageBasedOnReportInfo(
        this.page === PageEnum.Loader ? PageEnum.Basic : this.page
      );
    },
    /**
     * @param {number} inactivityTime Doba "neaktivity" po které se dialog zavře. [ms]
     */
    onOpen(inactivityTime) {
      const { serverConnectionMonitor } = $root(this);
      // Z bezpečnosntého hľadiska sa "zapína" len pri otvorenom dialógu,
      // aby sa zbytočne nevyťažoval server.
      serverConnectionMonitor.on(
        'connectionChanged',
        this.onServerConnectionChanged
      );
      this.serverInfo = serverConnectionMonitor.getServerInfo();

      this.setInactivityThreshold(inactivityTime);
      this.show = true;
      // Ak už je report načítaný, nastaví sa stránka na základnú.
      this.setPageBasedOnReportInfo(PageEnum.Basic);
      // Best-effort pokus o zobrazení reportu, pokud se ho
      // ještě nepodařilo načíst.
      if (this.page === PageEnum.Loader) {
        this.fetchReport().then(() =>
          this.setPageBasedOnReportInfo(PageEnum.Basic)
        );
      }
      this.$nextTick(() => {
        const closeButton = this.$refs.closeButton;
        if (closeButton instanceof HTMLElement) {
          closeButton.focus();
        }
      });
    },
    /**
     * @param {ServerInfo} serverInfo
     */
    onServerConnectionChanged(serverInfo) {
      this.serverInfo = serverInfo;
    },
    /**
     * Nastaví prahovou hodnotu neaktivity, po které se dialog zavře.
     * @private
     * @param {number} inactivityTime  Doba "neaktivity" po které se dialog zavře. [ms]
     */
    setInactivityThreshold(inactivityTime) {
      this.inactivityThreshold = new Date().getTime() + inactivityTime;
    },
    /**
     * @param {PageEnum} page
     */
    setPageBasedOnReportInfo(page) {
      if (!this.reportInfo) {
        this.page = PageEnum.Loader;
        return;
      }
      this.page = page;
    }
  },
  computed: {
    basicInfo() {
      // Ak sa zmení reportInfo, prepočíta sa aj basicInfo.
      if (this.reportInfo) {
        return CommunicatorReport.generateBasicReport(
          this.reportInfo,
          this.serverInfo,
          $root(this).session,
          { currentTimeMillis: this.currentTimeMillis }
        );
      }
    }
  },
  emits: {},
  props: {
    className: { type: String, require: true },
    contentInfo: {
      /** @type {PropType<ContentInfo | null>} */
      type: Object,
      required: false
    }
  }
});

/**
 * Typově bezpečná operace přidání třídy k elementu.
 * @param {unknown} elem
 * @param {string} className
 */
const addClassIfHTMLElement = (elem, className) => {
  if (elem instanceof HTMLElement) {
    elem.classList.add(className);
  }
};

/**
 * Typově bezpečná operace odstranění třídy z elementu.
 * @param {unknown} elem
 * @param {string} className
 */
const removeClassIfHTMLElement = (elem, className) => {
  if (elem instanceof HTMLElement) {
    elem.classList.remove(className);
  }
};
